package com.arena_web.ui.components.overlay

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.events.PopupOverlayFadeIn
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.GhostPillButton
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.sections.profile.edit.PrimaryOrGhostPillButton
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

/**
 * Shown on the People page when the caller tries to swipe / like / skip / tap a
 * card before their own profile is ready. The primary CTA hands control off to
 * the 3-step CompleteProfileWizardDialog.
 *
 * Design: desktop — Figma 7891:304548; mobile — Figma 8383:187719 (8171:175217)
 * — lock illustration on top, centered title + description, then a "Maybe later"
 * ghost pill + "Complete profile" primary pill side-by-side. The two layouts
 * differ in chrome (the mobile card is bordered and tighter), so they're built
 * separately: the desktop card scales via `s()`, the mobile card via `m()` (the
 * 390-px-baseline scale that stays correct behind an `isMobile()` gate).
 */
@Composable
fun CompleteProfilePromptDialog(
    isOpen: Boolean,
    onCancel: () -> Unit,
    onCompleteProfile: () -> Unit,
) {
    if (!isOpen) return

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1050)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onCancel() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut,
                )
            ),
        contentAlignment = Alignment.Center,
    ) {
        if (isMobile()) {
            MobilePromptCard(onCancel = onCancel, onCompleteProfile = onCompleteProfile)
        } else {
            DesktopPromptCard(onCancel = onCancel, onCompleteProfile = onCompleteProfile)
        }
    }
}

// Desktop — Figma 7891:304548. 560-px card, generous padding, no border.
@Composable
private fun DesktopPromptCard(onCancel: () -> Unit, onCompleteProfile: () -> Unit) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .sBorderRadius(18)
            .onClick { it.stopPropagation() }
            .styleModifier {
                property("width", "min(${s(560)}, 92vw)")
                property("background-color", "var(--l-s2, #09142d)")
                property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                property("overflow", "clip")
            }
            .sPadding(v = 40, h = 48)
            .sGap(24),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Icon(
            src = AppIcons.LockPeople,
            modifier = Modifier.sWidth(80).sHeight(105),
        )

        Column(
            modifier = Modifier.fillMaxWidth().sGap(40),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().sGap(16),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SpanText(
                    t.completeProfileTitle,
                    modifier = Modifier
                        .fillMaxWidth()
                        .sFontSize(24)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(32)
                        .sLetterSpacing(0.24)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("text-align", "center")
                            property("word-break", "break-word")
                        },
                )
                SpanText(
                    t.completeProfileDescription,
                    modifier = Modifier
                        .fillMaxWidth()
                        .sFontSize(16)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(24)
                        .sLetterSpacing(0.16)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                        .styleModifier { property("text-align", "center") },
                )
            }

            Row(
                modifier = Modifier.sGap(12),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                GhostPillButton(t.completeProfileMaybeLater, onClick = onCancel)
                PrimaryOrGhostPillButton(
                    t.completeProfileAction,
                    primary = true,
                    onClick = onCompleteProfile,
                )
            }
        }
    }
}

// Mobile — Figma 8171:175217. 360-px bordered card, tighter padding; sized with
// the m() family so it stays full-size on a 390-px phone and shrinks below that
// (s()-scaled values would be clamped to 0.4 here and render far too small).
// A close disc in the top-right corner replaces the old "Maybe later" pill.
@Composable
private fun MobilePromptCard(onCancel: () -> Unit, onCompleteProfile: () -> Unit) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .onClick { it.stopPropagation() }
            .styleModifier {
                property("position", "relative")
                property("width", "min(${m(360)}, calc(100vw - ${m(30)}))")
                property("box-sizing", "border-box")
                property("background-color", "var(--l-s2, #09142d)")
                property("border", "1px solid rgba(60, 121, 241, 0.45)")
                property("border-radius", m(22))
                property("padding", "${m(16)} ${m(20)} ${m(22)}")
            }
            .mGap(26),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        MobileCloseButton(onClick = onCancel)

        Icon(
            src = AppIcons.LockPeople,
            modifier = Modifier.mWidth(60).mHeight(79),
        )

        Column(
            modifier = Modifier.fillMaxWidth().mGap(28),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().mGap(16),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SpanText(
                    t.completeProfileTitle,
                    modifier = Modifier
                        .fillMaxWidth()
                        .mFontSize(20)
                        .fontWeight(AppTypography.Medium)
                        .mLineHeight(26)
                        .mLetterSpacing(0.2)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("text-align", "center")
                            property("word-break", "break-word")
                        },
                )
                SpanText(
                    t.completeProfileDescription,
                    modifier = Modifier
                        .fillMaxWidth()
                        .mFontSize(16)
                        .fontWeight(AppTypography.Regular)
                        .mLineHeight(24)
                        .mLetterSpacing(0.16)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                        .styleModifier { property("text-align", "center") },
                )
            }

            MobilePrimaryPill(t.completeProfileAction, onClick = onCompleteProfile)
        }
    }
}

// Close — translucent disc with the shared gradient ring, pinned to the
// card's top-right corner (mirrors the PersonDetailDialog mobile close).
@Composable
private fun MobileCloseButton(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .mSize(36)
            .cursor(Cursor.Pointer)
            .onClick {
                it.stopPropagation()
                onClick()
            }
            .styleModifier {
                property("position", "absolute")
                property("top", m(12))
                property("right", m(12))
                property("background", AppGradients.InteractiveSecondary)
                property("border-radius", m(100))
                property("z-index", "1")
            },
        contentAlignment = Alignment.Center,
    ) {
        GradientBorderRing(radiusPx = 100)
        Icon(src = AppIcons.Close, modifier = Modifier.mSize(14))
    }
}

// "Complete profile" — centred primary blue pill sized to its label.
@Composable
private fun MobilePrimaryPill(label: String, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .mHeight(44)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("padding", "0 ${m(32)}")
                property("background-color", "#0061FF")
                property("border-radius", m(100))
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .mFontSize(16)
                .fontWeight(AppTypography.Medium)
                .mLineHeight(18)
                .mLetterSpacing(0.032)
                .color(Color.white),
        )
    }
}
